import { useState } from "react";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Button } from "./button";

/**
 * DeleteConfirmationModal - Shared centered delete confirmation dialog
 * 
 * Usage:
 * <DeleteConfirmationModal
 *   open={showDelete}
 *   onOpenChange={setShowDelete}
 *   title="Delete Owner"
 *   description="Are you sure you want to delete this owner?"
 *   warningMessage="This owner has historical records..."
 *   itemName="John Smith"
 *   onConfirm={handleDelete}
 *   isLoading={deleting}
 * />
 */

const DeleteConfirmationModal = ({
  open,
  onOpenChange,
  title = "Confirm Delete",
  description = "Are you sure you want to delete this item? This action cannot be undone.",
  warningMessage = null,
  itemName = null,
  itemType = "item",
  onConfirm,
  isLoading = false,
  deleteButtonText = "Delete",
  cancelButtonText = "Cancel",
  relatedRecords = null,
}) => {
  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-md"
        data-testid="delete-confirmation-modal"
      >
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-7 w-7 text-red-600" />
          </div>
          <DialogTitle className="text-center text-xl">{title}</DialogTitle>
          <DialogDescription className="text-center pt-2">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Item name being deleted */}
          {itemName && (
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-sm text-muted-foreground">{itemType}:</p>
              <p className="font-medium text-lg">{itemName}</p>
            </div>
          )}

          {/* Related records warning */}
          {relatedRecords && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Related Records Found
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {relatedRecords.payments > 0 && (
                  <div className="text-amber-700">Payments: {relatedRecords.payments}</div>
                )}
                {relatedRecords.notices > 0 && (
                  <div className="text-amber-700">Notices: {relatedRecords.notices}</div>
                )}
                {relatedRecords.drc_submissions > 0 && (
                  <div className="text-amber-700">DRC: {relatedRecords.drc_submissions}</div>
                )}
                {relatedRecords.correspondence > 0 && (
                  <div className="text-amber-700">Correspondence: {relatedRecords.correspondence}</div>
                )}
                {relatedRecords.properties > 0 && (
                  <div className="text-amber-700">Properties: {relatedRecords.properties}</div>
                )}
                {relatedRecords.work_orders > 0 && (
                  <div className="text-amber-700">Work Orders: {relatedRecords.work_orders}</div>
                )}
              </div>
            </div>
          )}

          {/* Warning message */}
          {warningMessage && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">{warningMessage}</p>
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-center gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            data-testid="delete-cancel-btn"
          >
            {cancelButtonText}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading}
            data-testid="delete-confirm-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                {deleteButtonText}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { DeleteConfirmationModal };
